import type { InterviewTemplate } from "../templates/types";
import type { FollowupState, InputMode, Locale, SafetyState, Turn, TurnResponse } from "../engine/types";

/**
 * Same-origin only (§18) — one Rails process serves the SPA and the API,
 * so there is no CORS surface and no VITE_API_URL to configure or leak.
 */
const BASE = "/api";

export class ApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export type DataPolicy = "unspecified" | "paid_tier_no_training" | "free_tier_may_train" | "custom";

export type HealthResponse = {
  mode: "live" | "demo";
  model: string;
  key_configured: boolean;
  budget_remaining: number;
  data_policy: DataPolicy;
  data_policy_text: string | null;
};

export type StartResponse = {
  interview_id: string;
  template_digest: string;
  continuity_token: string;
};

export type FieldSummaryEntry = {
  field_id: string;
  status: string;
  value: unknown;
  asked_count?: number;
  required?: boolean;
};

export type TurnRequestPayload = {
  locale: Locale;
  continuity_token: string;
  participant_turn_count: number;
  template: InterviewTemplate;
  field_summary: FieldSummaryEntry[];
  target_field_id: string | null;
  transcript: Array<{ role: Turn["role"]; text: string }>;
  latest_participant_message: string;
};

export type FollowupRequestPayload = {
  continuity_token: string;
  participant_turn_count: number;
  template: InterviewTemplate;
  locale: Locale;
  field_summary: FieldSummaryEntry[];
};

// The server requires the continuity token here (as for /followup) so it can
// refuse a model call for a paused check-in. The client also disables the
// button while paused, but the server is the one that decides.
export type SummaryRequestPayload = {
  locale: Locale;
  field_summary: FieldSummaryEntry[];
  continuity_token: string;
  participant_turn_count: number;
  template: InterviewTemplate;
};

/**
 * POST /api/summary response — a summary is always tagged ai_generated: true
 * (Tier 3, §5, §8). Since round 4 a safety outcome on /summary (a disclosure in
 * the values, or a model refusal) is the same pause /turn returns: no summary,
 * the fixed message, and a paused continuity token the client must adopt so
 * that no later request reaches the model.
 */
export type SummaryResponse =
  | { summary: string; ai_generated: true; meta: { provider: string } }
  | {
      summary: null;
      ai_generated: false;
      safety: SafetyState & { status: "paused" };
      followup: FollowupState;
      continuity_token: string;
      meta: { provider: null };
    };

export type FollowupResponse = {
  followup: FollowupState;
  safety: { status: "normal" | "paused"; category: string | null };
  continuity_token: string;
  meta: { provider: string | null; llm_called: boolean };
};

/**
 * POST /api/design/schema response (§12). Deliberately has NO
 * template_digest — that is computed server-side only, and only after the
 * mandatory human review step, never on a raw proposal like this one (§7).
 */
export type DesignResponse = {
  template: InterviewTemplate;
  notice: string;
  meta: { provider: string };
};

async function postJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

  const data: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? "unknown_error", err?.message ?? "Request failed.");
  }

  return data as T;
}

export const api = {
  health: async (signal?: AbortSignal): Promise<HealthResponse> => {
    const res = await fetch(`${BASE}/health`, { signal });
    if (!res.ok) throw new ApiError(res.status, "unknown_error", "Could not reach the server.");
    return res.json();
  },

  start: (template: InterviewTemplate, locale: Locale, signal?: AbortSignal) =>
    postJson<StartResponse>("/interview/start", { template, locale }, signal),

  summary: (payload: SummaryRequestPayload, signal?: AbortSignal) =>
    postJson<SummaryResponse>("/summary", payload, signal),

  turn: (payload: TurnRequestPayload, signal?: AbortSignal) =>
    postJson<TurnResponse>("/interview/turn", payload, signal),

  followup: (payload: FollowupRequestPayload, signal?: AbortSignal) =>
    postJson<FollowupResponse>("/interview/followup", payload, signal),

  design: (text: string, signal?: AbortSignal) =>
    postJson<DesignResponse>("/design/schema", { text }, signal),
};

export type { InputMode };
